import { useRef, useState, type ChangeEvent, type JSX } from 'react';
import { api, formatBytes, type UploadedAsset } from '../api.ts';

/**
 * Picks files and uploads them one at a time, returning the assets that stuck.
 *
 * Upload happens on selection rather than on submit. A student who attaches a 40 MB archive and
 * only learns it was rejected after writing their answer has lost both; this way the refusal
 * arrives while they are still looking at the file picker.
 *
 * The limits are checked here as well as on the server. That is not duplication for its own sake:
 * the client check is the one that produces a useful message instead of a failed request.
 */

interface FileUploadProps {
  readonly maxFiles: number;
  readonly maxFileMb: number;
  readonly files: readonly UploadedAsset[];
  readonly onChange: (files: UploadedAsset[]) => void;
  readonly disabled?: boolean;
  readonly label?: string;
}

export function FileUpload({
  maxFiles,
  maxFileMb,
  files,
  onChange,
  disabled = false,
  label = 'Прикрепить файлы',
}: FileUploadProps): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const full = files.length >= maxFiles;

  async function pick(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const chosen = [...(event.target.files ?? [])];
    // Clearing the input lets the same file be picked again after a removal.
    event.target.value = '';

    if (chosen.length === 0) return;

    setError('');

    if (files.length + chosen.length > maxFiles) {
      setError(`Можно приложить не больше ${maxFiles} файлов.`);
      return;
    }

    const tooBig = chosen.find((file) => file.size > maxFileMb * 1024 * 1024);
    if (tooBig) {
      setError(`Файл «${tooBig.name}» больше ${maxFileMb} МБ.`);
      return;
    }

    setBusy(true);
    const accepted: UploadedAsset[] = [];

    try {
      for (const file of chosen) {
        accepted.push(await api.upload(file));
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить файл');
    } finally {
      // Whatever got through is kept: a failure on the third file must not discard the first two.
      if (accepted.length > 0) onChange([...files, ...accepted]);
      setBusy(false);
    }
  }

  function remove(assetId: string): void {
    onChange(files.filter((f) => f.id !== assetId));
  }

  return (
    <div className="uploader">
      <input
        ref={input}
        type="file"
        multiple={maxFiles > 1}
        hidden
        onChange={(e) => void pick(e)}
      />

      <div className="row">
        <button
          type="button"
          className="btn btn--ghost btn--small"
          disabled={disabled || busy || full}
          onClick={() => input.current?.click()}
        >
          {busy ? 'Загружаем…' : `📎 ${label}`}
        </button>
        <span className="muted" style={{ fontSize: '0.82rem' }}>
          до {maxFiles} шт., не больше {maxFileMb} МБ каждый
        </span>
      </div>

      {error ? <p className="error">{error}</p> : null}

      {files.length > 0 ? (
        <ul className="filelist">
          {files.map((file) => (
            <li key={file.id}>
              <span className="filelist__name">{file.original_name}</span>
              <span className="muted">{formatBytes(file.size_bytes)}</span>
              {/* Only a file still awaiting a verdict is flagged; a skipped scan is not news. */}
              {file.scan_status === 'pending' ? (
                <span className="badge badge--wait">проверяется</span>
              ) : null}
              {!disabled ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  onClick={() => remove(file.id)}
                  aria-label={`Убрать ${file.original_name}`}
                >
                  ✕
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
